// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase, IconFileDescription, IconFileInvoice, IconCalendar,
  IconChevronRight, IconMapPin, IconCalendarEvent,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import PageContext from '@/utils/page-context';
import { deriveStatusTone, getJobAppointment } from '../CustomerZoneShared';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import styles from '../CustomerZoneDetail.module.css';

const TABS = [
  { key: 'jobs',         label: 'Jobs',         Icon: IconBriefcase },
  { key: 'quotes',       label: 'Quotes',       Icon: IconFileDescription },
  { key: 'invoices',     label: 'Invoices',     Icon: IconFileInvoice },
  { key: 'appointments', label: 'Appointments', Icon: IconCalendar },
];

function Pill({ text, tone }: { text: string; tone: string }) {
  const cls = { ok: styles.pillOk, warn: styles.pillWarn, danger: styles.pillDanger, info: styles.pillInfo }[tone] || styles.pillInfo;
  return <span className={`${styles.pill} ${cls}`}>{text}</span>;
}

function JobCard({ job, now, href, onClick }: any) {
  const appt = getJobAppointment(job, now);
  const statusText = job.JobCardStatusDescription || (job._closed ? 'Closed' : 'Open');
  const tone = job._closed ? 'ok' : deriveStatusTone(statusText);
  return (
    <a className={styles.miniCard} href={href} onClick={(e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      onClick();
    }}>
      <div className={styles.miniHead}>
        <span className={styles.miniTitle}>{job.JobCardNumber}</span>
        <Pill text={statusText} tone={tone} />
      </div>
      <div className={styles.miniRows}>
        {job.Location?.AddressLine1 && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconMapPin size={13} /></span>
            <span>{job.Location.AddressLine1}</span>
          </div>
        )}
        {appt && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconCalendarEvent size={13} /></span>
            <span>{Time.toISOString(Time.parseDate(appt.StartDateTime), false, true, false)}</span>
          </div>
        )}
      </div>
    </a>
  );
}

function Group({ label, tone, jobs, now, getHref, onCard, hasMore, onLoadMore, isFetching }: any) {
  if (!jobs.length) return null;
  const cls = { success: styles.groupLabelSuccess, ok: styles.groupLabelOk, warn: styles.groupLabelWarn, danger: styles.groupLabelDanger, info: styles.groupLabelInfo }[tone] || styles.groupLabelInfo;
  return (
    <div className={styles.listGroup}>
      <div className={`${styles.groupLabel} ${cls}`}>
        {label}<span className={styles.gCount}>({jobs.length})</span>
      </div>
      <div className={styles.grid}>
        {jobs.map((job: any, i: number) => (
          <JobCard key={i} job={job} now={now} href={getHref(job)} onClick={() => onCard(job)} />
        ))}
      </div>
      {hasMore && (
        <div className={styles.loadMore}>
          <button className={styles.loadMoreBtn} onClick={onLoadMore} disabled={isFetching}>
            {isFetching ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function CustomerZoneJobListPage() {
  const router = useRouter();
  const { t, c } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openPage, setOpenPage] = useState(0);
  const [closedPage, setClosedPage] = useState(0);
  const [allOpenJobs, setAllOpenJobs] = useState<any[]>([]);
  const [allClosedJobs, setAllClosedJobs] = useState<any[]>([]);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: now } = useQuery(
    ['czNow', t, c],
    () => Time.nowCZ(t, c, tenant.API),
    { enabled: !!tenant }
  );

  const { data: openData, isFetching: fetchingOpen } = useQuery(
    ['czJobListOpen', t, c, openPage],
    () => Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: 50, PageIndex: openPage, SortExpression: 'StartDate', SortDirection: 'descending', OnlyClosed: false }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  const { data: closedData, isFetching: fetchingClosed } = useQuery(
    ['czJobListClosed', t, c, closedPage],
    () => Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: 50, PageIndex: closedPage, SortExpression: 'StartDate', SortDirection: 'descending', OnlyClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  useEffect(() => {
    if (!openData?.Results) return;
    const newItems = openData.Results.map((j: any) => ({ ...j, _closed: false }));
    if (openPage === 0) setAllOpenJobs(newItems);
    else setAllOpenJobs(prev => [...prev, ...newItems]);
  }, [openData]);

  useEffect(() => {
    if (!closedData?.Results) return;
    const newItems = closedData.Results.map((j: any) => ({ ...j, _closed: true }));
    if (closedPage === 0) setAllClosedJobs(newItems);
    else setAllClosedJobs(prev => [...prev, ...newItems]);
  }, [closedData]);

  useEffect(() => {
    const name = openData?.headerTenantName || tenant?.Name;
    if (name) pageContext?.updateTenantName?.(name);
  }, [openData, tenant]);

  if (!tenant) return null;

  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';

  const hasMoreOpen = (openData?.Results?.length ?? 0) >= 50;
  const hasMoreClosed = (closedData?.Results?.length ?? 0) >= 50;
  const isEmpty = allOpenJobs.length === 0 && allClosedJobs.length === 0 && openData && closedData;

  const navigate = (tab: string) => {
    const pages: Record<string, string> = { jobs: 'joblist', quotes: 'quotelist', invoices: 'invoicelist', appointments: 'appointmentlist' };
    router.push(`/customerzone/${pages[tab]}${base}`);
  };

  const goToDetail = (job: any) => router.push(`/customerzone/viewjob${base}&i=${job.ID}`);

  return (
    <>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.switchWrap}>
              <div className={styles.cardToolbar}>
                <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                  <Link to="/customerzone" search={{ t, c }}>Portal</Link>
                  <span className={styles.bcSep}><IconChevronRight /></span>
                  <span className={styles.bcCurrent}>Jobs</span>
                </nav>
              </div>
              <div className={styles.switchTabs}>
                {TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    className={`${styles.switchTab}${key === 'jobs' ? ` ${styles.switchTabActive}` : ''}`}
                    onClick={() => navigate(key)}
                  >
                    <Icon size={16} /><span>{label}</span>
                  </button>
                ))}
              </div>
              <div className={`${styles.switchSelect}${mobileOpen ? ` ${styles.switchSelectOpen}` : ''}`}>
                <button className={styles.switchTrigger} onClick={() => setMobileOpen(o => !o)} type="button">
                  <span className={styles.switchTriggerLeft}>
                    <IconBriefcase size={15} />
                    <span>Viewing</span>
                    <span className={styles.switchTriggerCurrent}>Jobs</span>
                  </span>
                  <IconChevronRight className={styles.switchTriggerChev} size={14} />
                </button>
                <div className={styles.switchMenu}>
                  {TABS.map(({ key, label, Icon }) => (
                    <button key={key} className={`${styles.switchOpt}${key === 'jobs' ? ` ${styles.switchOptActive}` : ''}`} onClick={() => { setMobileOpen(false); navigate(key); }}>
                      <Icon size={17} /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.cardInner}>
              <h2 className={styles.contentHead}>Your Jobs</h2>
              {isEmpty ? (
                <p className={styles.emptyNote}>There are no jobs</p>
              ) : (
                <div className={styles.listGroups}>
                  <Group
                    label="Open" tone="success" jobs={allOpenJobs} now={now}
                    getHref={(job: any) => `/customerzone/viewjob${base}&i=${job.ID}`}
                    onCard={goToDetail}
                    hasMore={hasMoreOpen} onLoadMore={() => setOpenPage(p => p + 1)} isFetching={fetchingOpen}
                  />
                  <Group
                    label="Closed" tone="info" jobs={allClosedJobs} now={now}
                    getHref={(job: any) => `/customerzone/viewjob${base}&i=${job.ID}`}
                    onCard={goToDetail}
                    hasMore={hasMoreClosed} onLoadMore={() => setClosedPage(p => p + 1)} isFetching={fetchingClosed}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}
    </>
  );
}
